import localeRegistryData from "./shared/localeRegistry.json";

export const DEFAULT_LOCALE = localeRegistryData.defaultLocale;
export const LOCALE_STORAGE_KEY = "ferrysearch-preferred-locale";

const LOCALE_CONFIGS = Object.fromEntries(
  localeRegistryData.supportedLocales.map((config) => [config.locale, config]),
);

const COUNTRY_DEFAULT_LOCALES = Object.fromEntries(
  localeRegistryData.countries.map((country) => [country.code, country.defaultLocale]),
);

const LANGUAGE_DEFAULT_LOCALES = Object.fromEntries(
  localeRegistryData.languages.map((language) => [
    language.code,
    language.defaultLocale,
  ]),
);

const COUNTRY_ALIASES = {
  BE: "BE",
  BELGIUM: "BE",
  BELGIQUE: "BE",
  BELGIE: "BE",
  CH: "CH",
  CHE: "CH",
  DE: "DE",
  DEU: "DE",
  ES: "ES",
  ESP: "ES",
  FR: "FR",
  FRA: "FR",
  GB: "GB",
  GBR: "GB",
  GR: "GR",
  GRC: "GR",
  EL: "GR",
  IE: "IE",
  IRL: "IE",
  IT: "IT",
  ITA: "IT",
  NL: "NL",
  NLD: "NL",
  SCHWEIZ: "CH",
  SUISSE: "CH",
  SWITZERLAND: "CH",
  UK: "GB",
  UNITEDKINGDOM: "GB",
};

const LANGUAGE_ALIASES = {
  DE: "de",
  EL: "el",
  EN: "en",
  ES: "es",
  FR: "fr",
  GR: "el",
  IT: "it",
  NL: "nl",
};

export function normaliseLocale(input) {
  if (!input) {
    return "";
  }

  const candidate = String(input).trim().toLowerCase().replace(/_/g, "-");
  return LOCALE_CONFIGS[candidate] ? candidate : "";
}

export function normaliseCountryCode(input) {
  if (!input) {
    return "";
  }

  const cleaned = String(input).trim().toUpperCase().replace(/[^A-Z]/g, "");
  return COUNTRY_ALIASES[cleaned] || "";
}

export function normaliseLanguageCode(input) {
  if (!input) {
    return "";
  }

  const cleaned = String(input).trim().replace(/_/g, "-");
  const [baseLanguage] = cleaned.split("-");
  const normalisedBase = String(baseLanguage || "")
    .toUpperCase()
    .replace(/[^A-Z]/g, "");

  return LANGUAGE_ALIASES[normalisedBase] || String(baseLanguage || "").toLowerCase();
}

export function getLocaleConfig(locale) {
  return LOCALE_CONFIGS[normaliseLocale(locale)] || LOCALE_CONFIGS[DEFAULT_LOCALE];
}

export function getLocaleFromPath(pathname) {
  if (!pathname) {
    return "";
  }

  const [firstSegment] = String(pathname).split("/").filter(Boolean);
  return normaliseLocale(firstSegment);
}

export function getStoredLocale() {
  if (typeof window === "undefined") {
    return "";
  }

  try {
    return normaliseLocale(window.localStorage.getItem(LOCALE_STORAGE_KEY));
  } catch (error) {
    return "";
  }
}

export function persistLocale(locale) {
  if (typeof window === "undefined") {
    return;
  }

  const nextLocale = normaliseLocale(locale);

  try {
    if (nextLocale) {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, nextLocale);
    } else {
      window.localStorage.removeItem(LOCALE_STORAGE_KEY);
    }
  } catch (error) {
    // Ignore storage failures and keep locale state in memory.
  }
}

export function getQueryHints(search) {
  const params = new URLSearchParams(search || "");

  return {
    country: normaliseCountryCode(params.get("country")),
    language: normaliseLanguageCode(params.get("language")),
    locale: normaliseLocale(params.get("locale")),
  };
}

export function stripLocaleHintParams(search) {
  const params = new URLSearchParams(search || "");
  params.delete("country");
  params.delete("language");
  params.delete("locale");
  const nextSearch = params.toString();

  return nextSearch ? `?${nextSearch}` : "";
}

export function buildPathForLocale(locale, search = "") {
  const nextLocale = normaliseLocale(locale) || DEFAULT_LOCALE;
  const cleanedSearch = search ? (search.startsWith("?") ? search : `?${search}`) : "";

  return `/${nextLocale}${cleanedSearch}`;
}

function findExactLocale(countryCode, languageCode) {
  return localeRegistryData.supportedLocales.find(
    (config) =>
      config.countryCode === normaliseCountryCode(countryCode) &&
      config.languageCode === normaliseLanguageCode(languageCode),
  );
}

export function resolveLocaleForSelection({ countryCode, languageCode }) {
  const exactMatch = findExactLocale(countryCode, languageCode);
  if (exactMatch) {
    return exactMatch.locale;
  }

  const supportedCountryCode = normaliseCountryCode(countryCode);
  if (supportedCountryCode && COUNTRY_DEFAULT_LOCALES[supportedCountryCode]) {
    return COUNTRY_DEFAULT_LOCALES[supportedCountryCode];
  }

  const supportedLanguageCode = normaliseLanguageCode(languageCode);
  if (supportedLanguageCode && LANGUAGE_DEFAULT_LOCALES[supportedLanguageCode]) {
    return LANGUAGE_DEFAULT_LOCALES[supportedLanguageCode];
  }

  return DEFAULT_LOCALE;
}

export function resolveLocaleForCountryChange(countryCode, languageCode) {
  const exactMatch = findExactLocale(countryCode, languageCode);
  if (exactMatch) {
    return exactMatch.locale;
  }

  const supportedCountryCode = normaliseCountryCode(countryCode);
  if (supportedCountryCode && COUNTRY_DEFAULT_LOCALES[supportedCountryCode]) {
    return COUNTRY_DEFAULT_LOCALES[supportedCountryCode];
  }

  const supportedLanguageCode = normaliseLanguageCode(languageCode);
  if (supportedLanguageCode && LANGUAGE_DEFAULT_LOCALES[supportedLanguageCode]) {
    return LANGUAGE_DEFAULT_LOCALES[supportedLanguageCode];
  }

  return DEFAULT_LOCALE;
}

export function resolveLocaleForLanguageChange(countryCode, languageCode) {
  const exactMatch = findExactLocale(countryCode, languageCode);
  if (exactMatch) {
    return exactMatch.locale;
  }

  const supportedLanguageCode = normaliseLanguageCode(languageCode);
  if (supportedLanguageCode && LANGUAGE_DEFAULT_LOCALES[supportedLanguageCode]) {
    return LANGUAGE_DEFAULT_LOCALES[supportedLanguageCode];
  }

  const supportedCountryCode = normaliseCountryCode(countryCode);
  if (supportedCountryCode && COUNTRY_DEFAULT_LOCALES[supportedCountryCode]) {
    return COUNTRY_DEFAULT_LOCALES[supportedCountryCode];
  }

  return DEFAULT_LOCALE;
}

export function getClientBrowserLanguage() {
  if (typeof navigator === "undefined") {
    return "";
  }

  if (Array.isArray(navigator.languages) && navigator.languages.length > 0) {
    return navigator.languages.join(",");
  }

  return navigator.language || "";
}

export function getClientTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch (error) {
    return "";
  }
}

export function getInitialLocalePreference() {
  if (typeof window === "undefined") {
    return DEFAULT_LOCALE;
  }

  const pathLocale = getLocaleFromPath(window.location.pathname);
  if (pathLocale) {
    return pathLocale;
  }

  const queryHints = getQueryHints(window.location.search);
  if (queryHints.locale || queryHints.country || queryHints.language) {
    return "";
  }

  return getStoredLocale();
}
